import { Component } from '@theme/component';
import {
  CART_FETCH_TIMEOUT_MS,
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
  timeoutSignal,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';
import { buildPackAnchorProperties, buildGiftItems, resolveTierForQuantity, addPackItems } from '@theme/pack-tiers';
import { formatMoney } from '@theme/money-formatting';
import { fetchCartJson } from '@theme/utilities';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Un tier tal como lo emite `snippets/cart-item-row.liquid` en `data-pack-tiers`, ya resuelto
 * para el talle de esa fila. `price`/`compareAtPrice` son el TOTAL del tier para su propia
 * cantidad; `basePrice` y `pct` permiten calcular el de cualquier otra cantidad.
 * @typedef {object} PackTier
 * @property {number} quantity
 * @property {number} variantId
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {number} basePrice
 * @property {number} pct
 * @property {string} [imageUrl]
 * @property {PackTierGift[]} gifts
 */

/**
 * @typedef {object} PackTierGift
 * @property {number} variantId
 * @property {number} productId
 * @property {number} slot
 * @property {number} quantity
 * @property {boolean} [isBundle]
 * @property {string | null} [stockHandle]
 * @property {string} [size] - Sólo en un regalo de talle propio: el talle que eligió Liquid (el
 *   primero con stock para su cantidad). Vacío en los demás.
 */

/**
 * Una fila de REGALO tal como está en el DOM ahora mismo (ver los `data-*` de los dependientes en
 * `cart-item-row.liquid`).
 * @typedef {object} GiftRow
 * @property {string} key
 * @property {number} slot
 * @property {number} variantId
 * @property {number} productId
 * @property {number} quantity
 * @property {boolean} isBundle
 * @property {string | undefined} stockHandle
 * @property {boolean} ownSize
 */

/** Timer that force-clears the document-root busy flag — see `#syncGlobalBusyState`. Module
 * level because the flag itself is shared by every cart component on the page.
 *
 * Declared ABOVE the class on purpose: `customElements.define` upgrades already-parsed elements
 * synchronously, so a `connectedCallback` can run before the rest of this module has evaluated,
 * and a `let` declared further down would still be in its temporal dead zone at that point.
 * @type {ReturnType<typeof setTimeout> | undefined} */
let globalBusyReleaseTimer;

/**
 * A custom element that displays a cart items component.
 *
 * El modelo de packs, en dos líneas (el resto en `assets/pack-tiers.js`): la línea PAGA de un
 * pack es la variante base con cantidad N, y su precio lo pone un descuento automático de
 * Shopify. Cambiar de pack es cambiar la CANTIDAD de esa misma línea —su clave no se mueve— y
 * ajustar los regalos por diferencia. Sólo cambiar de TALLE sigue siendo quitar y agregar, porque
 * Shopify no deja cambiarle la variante a una línea.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** How long to coalesce repeated clicks on ONE stepper into a single cart request. */
  static #QUANTITY_DEBOUNCE_MS = 300;

  /** One debouncer PER quantity selector, instead of one shared by the whole document.
   *
   * A single shared debouncer means the timer is global: touching line A and then line B
   * within the window `clearTimeout`s A outright, so A's request is **never sent** — while A's
   * input has already been updated optimistically (`component-quantity-selector.js` writes the
   * value before dispatching and never rolls it back), so A silently snaps back to its old value
   * on the next morph with no error anywhere.
   *
   * Keyed by the dispatching element rather than by `cartLine`: line numbers are positions
   * that get renumbered on every morph (`cart-item-row.liquid`'s `data-line`), so two clicks
   * on the SAME row that straddle a morph would land in different buckets and stop coalescing
   * — exactly what the debounce exists to prevent. The element identity is stable for as long
   * as a window can be open, and a `WeakMap` needs no cleanup when a morph replaces the row.
   * @type {WeakMap<Element, ReturnType<typeof debounce>>} */
  #debouncersBySelector = new WeakMap();

  /** Live debouncers, so `disconnectedCallback` can cancel pending ones — a `WeakMap` isn't
   * enumerable. Holds elements weakly so a removed row is still collectable.
   * @type {Set<WeakRef<Element>>} */
  #liveDebouncerRefs = new Set();

  /** @param {Event} event */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    // Filtered HERE, before the debounce, not inside it. Inside, an event from a selector that
    // isn't ours (the PDP picker, quick-add, the other cart-items-component) would first reset
    // the shared timer and only then be discarded — swallowing a genuinely pending cart update.
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const selector = target.closest('cart-quantity-selector-component') ?? target;

    // Captured HERE, synchronously, while `target` is guaranteed to be the row that was
    // actually clicked — NOT re-derived later by `#onQuantityChange` from the event's raw
    // line NUMBER. `data-line` is a render-time position that gets renumbered on every morph
    // (see `#getRowByLine`), and the debounce below deliberately waits `#QUANTITY_DEBOUNCE_MS`
    // before `#onQuantityChange` runs — plenty of time for an EARLIER pack operation (e.g. a
    // gift line being added/removed, which changes total row count) to renumber every row
    // after it. Confirmed live: two separate packs of the SAME base product (so their
    // `data-pack-tiers` JSON is identical and can't cross-check anything) — clicking one
    // pack's stepper resolved, after the debounce, to the OTHER pack's row, which was then
    // operated on for real. Capturing the row's stable identity now closes this.
    const row = target.closest('[data-line]');
    const groupId = row instanceof HTMLElement ? row.dataset.packGroupId : undefined;
    const key = row instanceof HTMLElement ? row.dataset.key : undefined;

    // Paint the new state NOW, from data the browser already has, instead of leaving the old
    // price on screen for the whole round trip. Measured before this existed: the stepper's
    // number updated in 82 ms while the line price, the ×N, the unit price and the cart total
    // all took 2430 ms — so the shopper watched a stale price for ~2.4 s after every tap, and
    // the cart total in particular showed a confident, wrong number with no loading treatment
    // at all. The server's morph still has the final word; this only fills the gap.
    if (row instanceof HTMLElement) this.#paintPackRowOptimistically(row, event.detail.quantity);

    let debounced = this.#debouncersBySelector.get(selector);
    if (!debounced) {
      debounced = debounce(
        /** @param {{event: QuantitySelectorUpdateEvent, groupId?: string, key?: string}} pending */
        (pending) => this.#onQuantityChange(pending),
        CartItemsComponent.#QUANTITY_DEBOUNCE_MS
      );
      this.#debouncersBySelector.set(selector, debounced);
      this.#liveDebouncerRefs.add(new WeakRef(selector));
    }
    debounced({ event, groupId, key });
  };
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    this.#healBrokenPackLinesOnce();
  }

  /** Guards {@link #healBrokenPackLinesOnce} — a repair triggers a re-render, which can
   * reconnect this component, and repairing again from there would loop. */
  #healAttempted = false;

  /**
   * Repairs a cart that was ALREADY broken before this code ever ran.
   *
   * The reconciliation built into each operation stops new duplicates, but it only runs when
   * the customer does something. A cart that a previous version of this code corrupted would
   * otherwise keep showing its extra free gifts indefinitely — including at checkout. This
   * heals it on the next page view instead.
   *
   * Gated on the rendered markup actually containing a pack dependent (`cart-items__nested-line`,
   * see cart-item-row.liquid), so an ordinary cart costs no extra request at all.
   */
  #healBrokenPackLinesOnce() {
    if (this.#healAttempted) return;
    this.#healAttempted = true;
    if (!this.querySelector('.cart-items__nested-line')) return;

    this.#reconcilePackDependents()
      .then(({ removed }) => {
        if (removed > 0) {
          return sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch(/** @param {any} error */ (error) => console.error('[cart-items] Pack self-heal failed:', error));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    // Drop any still-pending debounced update — this component is going away, so firing would
    // operate on a detached tree.
    for (const ref of this.#liveDebouncerRefs) {
      const selector = ref.deref();
      if (selector) this.#debouncersBySelector.get(selector)?.cancel();
    }
    this.#liveDebouncerRefs.clear();

    // A component torn down mid-operation would otherwise leave the document-root busy flag
    // set, and with it a permanently dimmed, unclickable checkout button.
    delete this.dataset.cartBusy;
    this.#syncGlobalBusyState();
  }

  /**
   * Deja que cualquier operación de pack en vuelo termine antes de que el cliente se vaya al
   * checkout — la última guarda antes de irse (ver `installCheckoutGuard`).
   *
   * Ya no relee el carrito: cada operación reconcilia sus dependientes con la respuesta que ella
   * misma recibió, y un carrito que llega roto de antes lo cura `#healBrokenPackLinesOnce` al
   * cargar. Acotado: una verificación colgada nunca puede dejar el botón de pagar muerto.
   * @param {object} [options]
   * @param {number} [options.timeoutMs] - upper bound on waiting for in-flight work
   */
  async verifyPackIntegrity({ timeoutMs = 3000 } = {}) {
    await Promise.race([
      Promise.allSettled([this.#packOpTail]),
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {{event: QuantitySelectorUpdateEvent, groupId?: string, key?: string}} pending
   *   `groupId`/`key` are the clicked row's stable identity, captured synchronously at click
   *   time by `#onQuantitySelectorUpdate` — see the comment there for why re-deriving the row
   *   from `line` alone, HERE, after the debounce, is what let one pack's click operate on a
   *   DIFFERENT pack's row.
   */
  #onQuantityChange({ event, groupId, key }) {
    // Ownership was already checked when the event arrived (see `#onQuantitySelectorUpdate`).
    // Re-checking containment HERE would be wrong: during the debounce window a morph may have
    // re-rendered the row, so the original target is no longer inside `this` even though the
    // update is perfectly valid — that check would silently drop it. Only bail if this whole
    // component is gone.
    if (!this.isConnected) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Prefer the identity captured at click time — `line` alone can point at the wrong row by
    // now (see above). Fall back to the line-position lookup only when neither stable
    // identifier resolved (e.g. a row that's since been removed entirely).
    const row = this.#getRowByGroupId(groupId) ?? this.#getRowByKey(key) ?? this.#getRowByLine(line);
    const tiers = this.#getPackTiers(row);

    if (tiers) {
      // `groupId` (see pack-tiers.js) is the pack's one identity for its whole life, unlike
      // `line` — a snapshot of THIS row's position right now, stale as soon as an earlier queued
      // operation shifts things.
      this.#queuePackQuantity(row?.dataset.packGroupId ?? groupId, line, tiers, quantity);
    } else {
      // An ORDINARY (non-pack) line — but it still has to be targeted by its stable key and
      // serialized against pack operations, for the same reason pack lines are: a size change
      // removes a pack's lines before re-adding them, so for the span of that operation the
      // server's line numbering is shifted. A positional `{line: N}` update landing inside that
      // window edits whatever now sits at position N. Keying by `key` fixes the targeting;
      // chaining on `#packOpTail` also keeps the section re-renders from interleaving.
      const plainKey = row?.dataset.key ?? key;
      const next = Promise.allSettled([this.#packOpTail]).then(() =>
        this.updateQuantity({ line, ...(plainKey ? { key: plainKey } : {}), quantity, action: 'change' })
      );
      this.#packOpTail = next;
    }

    // Use the already-resolved `row`, not another `#getRowByLine(line)` re-lookup — same stale-
    // position risk as above, just for a cosmetic shimmer this time.
    if (!row) return;

    const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds the cart row for a given 1-based line number via its own `data-line` attribute,
   * rather than assuming `refs.cartItemRows[line - 1]` — pack gift rows are rendered
   * directly after their anchor (see `cart-products.liquid`), which is not necessarily their
   * raw position in `cart.items`, so DOM order and line number can diverge. Prefer
   * {@link #getRowByGroupId}/{@link #getRowByKey} wherever a stable identifier is available —
   * this is a last-resort fallback, not the primary lookup, for exactly that reason.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowByLine(line) {
    return this.refs.cartItemRows.find((row) => Number(row.dataset.line) === line);
  }

  /**
   * Finds a cart row by its stable `item.key` (rendered as `data-key`, see
   * `cart-item-row.liquid`) instead of its current line position — safe to use even after that
   * position has shifted, unlike {@link #getRowByLine}. Covers non-pack rows (no `groupId`),
   * which {@link #getRowByGroupId} can't resolve.
   * @param {string | undefined} key
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowByKey(key) {
    if (!key) return undefined;
    return this.refs.cartItemRows.find((row) => row.dataset.key === key);
  }

  /**
   * Finds a pack anchor row by its stable `_pack_group_id` (see pack-tiers.js) instead of its
   * current line position — safe to use even after that position has shifted since `line` was
   * last captured, unlike {@link #getRowByLine}.
   * @param {string | undefined} groupId
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowByGroupId(groupId) {
    if (!groupId) return undefined;
    return this.refs.cartItemRows.find((row) => row.dataset.packGroupId === groupId);
  }

  /**
   * Every row that must disappear together with `anchorRow` — the pack's gifts, plus any
   * native Shopify bundle children.
   *
   * Resolved from the DOM so a removal can delete the whole pack in ONE request instead of
   * reading the cart back just to learn which keys to delete. Dependents are matched by the
   * pack's stable group id (`data-pack-owner-group`, see cart-item-row.liquid) or by the
   * anchor's `_pack_id`.
   * @param {HTMLTableRowElement | undefined} anchorRow
   * @param {string | undefined} packId
   * @param {string | undefined} groupId
   * @returns {HTMLTableRowElement[]}
   */
  #getDependentRows(anchorRow, packId, groupId) {
    if (!anchorRow) return [];
    const anchorKey = anchorRow.dataset.key;
    return this.refs.cartItemRows.filter((row) => {
      if (row === anchorRow) return false;
      if (anchorKey && row.dataset.parentKey === anchorKey) return true;
      if (groupId && row.dataset.packOwnerGroup === groupId) return true;
      return Boolean(packId) && row.dataset.packParentId === packId;
    });
  }

  /**
   * Las filas de REGALO de un pack, tal como están en el DOM ahora mismo. Es lo que permite
   * ajustar los regalos por diferencia sin leer `/cart.json`: cada fila lleva su clave, su slot,
   * su variante, su producto y su cantidad (ver los `data-*` de los dependientes en
   * `cart-item-row.liquid`).
   * @param {HTMLTableRowElement | undefined} anchorRow
   * @param {string | undefined} groupId
   * @returns {GiftRow[]}
   */
  #getGiftRows(anchorRow, groupId) {
    return this.#getDependentRows(anchorRow, anchorRow?.dataset.packId, groupId)
      .filter((row) => row.dataset.packKind === 'gift' && row.dataset.key)
      .map((row) => ({
        key: /** @type {string} */ (row.dataset.key),
        slot: Number(row.dataset.giftSlot),
        variantId: Number(row.dataset.variantId) || 0,
        productId: Number(row.dataset.productId) || 0,
        quantity: Number(row.dataset.rawQuantity) || 1,
        isBundle: row.dataset.giftBundle === 'true',
        stockHandle: row.dataset.giftStockHandle || undefined,
        ownSize: row.dataset.giftOwnSize === 'true',
      }));
  }

  /**
   * Parses the `data-pack-tiers` JSON a cart row carries when its product has pack tiers
   * configured (see `snippets/cart-item-row.liquid`). Returns null for ordinary lines, so
   * callers can fall back to a plain quantity update.
   * @param {HTMLTableRowElement | undefined} row
   * @returns {PackTier[] | null}
   */
  #getPackTiers(row) {
    const raw = row?.dataset.packTiers;
    if (!raw) return null;

    try {
      const tiers = JSON.parse(raw);
      return Array.isArray(tiers) && tiers.length > 0 ? tiers : null;
    } catch (error) {
      console.error('[cart-items] Invalid data-pack-tiers JSON:', error);
      return null;
    }
  }

  /**
   * «Llevá 4, pagá 3: sumá 1 paquete» (`snippets/cart-pack-nudge.liquid`): UNA unidad más en la
   * línea del aviso, por la misma cola que el stepper, así no se cruza con un «+» en vuelo.
   * El grupo viene del botón y la fila se busca recién ahora: su `key` cambia cuando el descuento
   * empieza a aplicar, y la que valga es la del último morph (`.claude/rules/packs.md`).
   * @param {Event} event
   */
  onPackNudge(event) {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-pack-nudge-group]') : null
    );
    const groupId = button?.dataset.packNudgeGroup;
    const row = this.#getRowByGroupId(groupId);
    const tiers = this.#getPackTiers(row);
    if (!row || !tiers || !groupId) return;

    const next = (Number(row.dataset.rawQuantity) || 0) + 1;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    /** @type {any} */ (row.querySelector('text-component'))?.shimmer?.();
    this.#queuePackQuantity(groupId, Number(row.dataset.line) || 0, tiers, next);
  }

  /**
   * Paints a pack row's new state immediately, from data already in the page.
   *
   * Everything needed is local: `resolveTierForQuantity` decides the target tier with no
   * network at all, and `data-pack-tiers` carries the base price and the tier's percentage. The
   * number painted is EXACT for every quantity — precio base × cantidad menos el %, la misma
   * cuenta que hace Shopify al aplicar el descuento automático, así que el morph que llega
   * después lo reemplaza por el mismo número — and that is what lets `data-optimistic-paint`
   * cancel the "updating" dimming for the whole round trip (see cart-products.liquid).
   *
   * The quantity painted is the resolved one (`resolveTierForQuantity`: one unit at a time, the
   * tier is whichever that quantity reaches), so it matches what arrives.
   *
   * Bails out silently on anything it can't compute exactly — a non-pack row, markup rendered
   * before the base price was added to the JSON, a missing money format. Never guesses: a wrong
   * price on screen is worse than a slow one.
   * @param {HTMLElement} row
   * @param {number} requestedQuantity
   */
  #paintPackRowOptimistically(row, requestedQuantity) {
    try {
      if (!row.dataset.packGroupId) return;
      const tiers = this.#getPackTiers(/** @type {any} */ (row));
      if (!tiers) return;

      const selector = row.querySelector('cart-quantity-selector-component');
      const { tier, quantity } = resolveTierForQuantity(tiers, requestedQuantity);
      const basePrice = Number(tier.basePrice);
      const pct = Number(tier.pct) || 0;
      if (!Number.isFinite(basePrice) || basePrice <= 0 || quantity <= 0) return;

      const format = row.dataset.moneyFormat;
      const currency = row.dataset.currency;
      if (!format || !currency) return;
      const lineFormat = row.dataset.lineMoneyFormat || format;

      // Exactamente la cuenta de `pack-tier-price.liquid`: vale para CUALQUIER cantidad, también
      // por encima del tier mayor, porque el descuento "mínimo N" de Shopify no tiene techo.
      const listTotal = basePrice * quantity;
      const lineTotal = Math.round((listTotal * (100 - pct)) / 100);
      const perUnit = Math.round(lineTotal / quantity);
      // El tachado por unidad: el compare-at del producto si lo tiene, si no el precio de lista —
      // la misma elección que hace la fila en Liquid.
      const compareAtTotal = Number(tier.compareAtPrice) || 0;
      const comparePerUnit =
        compareAtTotal > 0 && tier.quantity > 0 ? Math.round(compareAtTotal / tier.quantity) : basePrice;

      const input = selector?.querySelector('input');
      if (input instanceof HTMLInputElement) input.value = String(quantity);

      const lineEl = row.querySelector('.cart-items__price text-component');
      this.#setTextComponent(lineEl, formatMoney(lineTotal, lineFormat, currency));
      // El tachado del total es el "antes" de la línea: compare-at por cantidad, la misma
      // referencia que el chip y que la fila en Liquid.
      const compareTotal = comparePerUnit * quantity;
      const lineOriginalEl = row.querySelector('.cart-items__line-original');
      if (lineOriginalEl instanceof HTMLElement) {
        lineOriginalEl.textContent = formatMoney(compareTotal, lineFormat, currency);
        lineOriginalEl.hidden = lineTotal >= compareTotal;
      }

      // The per-unit block: first non-screenreader span is the live price, the <s> beside it
      // is the crossed-out compare-at. Untouched when the row is a gift (no such markup).
      const unitWrapper = row.querySelector('.cart-items__unit-price-wrapper');
      const priceSpan = unitWrapper
        ? [...unitWrapper.querySelectorAll('span')].find((span) => !span.classList.contains('visually-hidden'))
        : null;
      if (priceSpan) priceSpan.textContent = formatMoney(perUnit, format, currency);
      const compareEl = unitWrapper?.querySelector('s.compare-at-price');
      if (compareEl && comparePerUnit > perUnit) compareEl.textContent = formatMoney(comparePerUnit, format, currency);

      // El chip "45% OFF": el ahorro de la línea entera contra el tachado, truncado como lo hace
      // `discount-percent.liquid` (división entera), para que el número pintado sea el que después
      // trae el servidor.
      const chipEl = unitWrapper?.querySelector('.cart-items__discount');
      if (chipEl instanceof HTMLElement) {
        const chipPct = compareTotal > lineTotal ? Math.floor(((compareTotal - lineTotal) * 100) / compareTotal) : 0;
        chipEl.textContent = `${chipPct}% OFF`;
        chipEl.hidden = chipPct <= 0;
      }

      if (tier.imageUrl && tier.imageUrl !== row.dataset.packImage) {
        const image = row.querySelector('.cart-items__media img');
        if (image instanceof HTMLImageElement) image.src = tier.imageUrl;
      }

      // Cart total, adjusted by this line's own delta. Deliberately arithmetic on the previous
      // total rather than a recount: a cart-level discount or shipping line would make a
      // from-scratch sum wrong, whereas a delta stays right for everything except a discount
      // whose own value depends on the total — and the server corrects even that on arrival.
      const previousLineTotal = Number(row.dataset.linePriceCents);
      const totalEl = this.refs.cartTotal;
      if (totalEl instanceof HTMLElement) {
        const previousTotal = Number(totalEl.dataset.totalCents);
        const totalFormat = totalEl.dataset.moneyFormat;
        if (Number.isFinite(previousLineTotal) && Number.isFinite(previousTotal) && totalFormat) {
          const nextTotal = Math.max(0, previousTotal + (lineTotal - previousLineTotal));
          this.#setTextComponent(totalEl, formatMoney(nextTotal, totalFormat, currency));
          totalEl.dataset.totalCents = String(nextTotal);
        } else {
          /** @type {any} */ (totalEl).shimmer?.();
        }
      }

      // Only the painter's own baseline moves, so a second tap before the server replies
      // computes its delta from what is on screen rather than from the stale original.
      //
      // `data-raw-quantity` is deliberately NOT touched. It is the SERVER's quantity, and
      // `#applyPackQuantity` reads it to decide what actually changed — writing the optimistic
      // value there would make the queued operation believe the change had already been
      // applied and take its no-op short circuit, so the price would update on screen and
      // never be sent. The whole point of painting early is that the request still happens.
      row.dataset.linePriceCents = String(lineTotal);
      this.dataset.optimisticPaint = 'exact';
    } catch (error) {
      // Never let a painting problem block the real cart operation that follows.
      console.error('[cart-items] Optimistic paint failed:', error);
    }
  }

  /**
   * Writes text into one of Horizon's `<text-component>` elements, keeping its `value`
   * attribute in step so anything that reads it later sees the same number.
   * @param {Element | null} element
   * @param {string} text
   */
  #setTextComponent(element, text) {
    if (!(element instanceof HTMLElement)) return;
    element.textContent = text;
    element.setAttribute('value', text);
  }

  /** Serializes pack operations PER PACK, keyed by `groupId` (see pack-tiers.js), NOT by `line`:
   * a cart position, unlike `groupId`, doesn't stay pointed at the same pack for the pack's
   * whole lifetime — an EARLIER queued operation can shift `cart.items` order before a LATER one
   * (captured with the `line` value from back when it was clicked) actually runs. Confirmed
   * live with the line-keyed version: duplicate/orphaned lines under repeated fast adjustments.
   * @type {Map<string, Promise<void>>} */
  #packQueue = new Map();

  /** Tail of ALL pack operations, regardless of which pack they belong to.
   *
   * `#packQueue` only serializes a pack against itself. With a per-selector debounce (see
   * `#debouncersBySelector`) two different packs can be adjusted within one window, and two
   * operations interleaving their requests against a single shared cart is how one of them ends
   * up reading the other's half-applied state. Chaining every pack operation here keeps them
   * globally serial; plain (non-pack) line updates only wait for the tail.
   * @type {Promise<any>} */
  #packOpTail = Promise.resolve();

  /**
   * El cliente eligio otro talle para una linea de pack.
   *
   * Shopify **no deja cambiarle la variante a una linea**: `/cart/change.js` solo acepta cantidad
   * y propiedades. Asi que por debajo esto es quitar-y-agregar (`#applyPackSize`).
   *
   * `event.target` SI es el select: el atributo `on:change` vive en el select mismo. Cuando el
   * atributo vive en un contenedor, `component.js` proxea `target` al contenedor y esto no
   * funcionaria (ver `handleSizeChange` en purchase-options.js).
   * @param {number} line
   * @param {Event} event
   */
  onPackSizeChange(line, event) {
    const select = event?.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const newSize = select.value;
    const previousSize = select.dataset.currentSize;
    if (!newSize || newSize === previousSize) return;

    const groupId = select.dataset.packGroupId || undefined;
    // Se deshabilita en el acto: el cambio tarda, y un segundo cambio a mitad de camino encolaria
    // una operacion que parte de un estado que ya no existe.
    select.disabled = true;

    // Una fila de REGALO de talle propio toma su propio camino: se toca solo la linea del regalo,
    // nunca la linea paga del cliente.
    const giftSlot = select.dataset.giftSlot;
    const pending =
      giftSlot == null
        ? this.#queuePackSize(groupId, line, newSize, previousSize)
        : this.#queueGiftSize(groupId, line, newSize, previousSize, Number(giftSlot));

    pending.finally(() => {
      // El repintado de la seccion suele reemplazar este mismo nodo; el `finally` cubre el caso
      // en que no lo haga (un fallo temprano), para que el selector no quede muerto.
      select.disabled = false;
    });
  }

  /**
   * Misma cola que las operaciones de cantidad — y la MISMA, no una paralela: un cambio de talle y
   * un toque al stepper sobre el mismo pack tienen que serializarse entre si o cada uno leeria el
   * estado a medio aplicar del otro.
   * @param {string | undefined} groupId
   * @param {number} line
   * @param {string} newSize
   * @param {string | undefined} previousSize
   */
  #queuePackSize(groupId, line, newSize, previousSize) {
    const queueKey = groupId ?? `line:${line}`;
    const previousForPack = this.#packQueue.get(queueKey) ?? Promise.resolve();
    const next = Promise.allSettled([previousForPack, this.#packOpTail]).then(() =>
      this.#applyPackSize(queueKey, line, newSize, previousSize)
    );
    this.#packQueue.set(queueKey, next);
    this.#packOpTail = next;
    return next;
  }

  /**
   * Cambia el talle de un pack sin tocar su cantidad. Siempre a traves de {@link #queuePackSize}.
   *
   * Es quitar el ancla y volver a agregarla en la variante del talle nuevo, con la MISMA cantidad
   * y las mismas propiedades salvo `_pack_size`. Los regalos que ESPEJAN el talle del pack van en
   * el mismo viaje, resueltos al talle nuevo con el mapa que la fila ya trae
   * (`data-pack-size-map`); un regalo de talle PROPIO no se toca —es una decision del cliente— y
   * su vinculo con el pack (`_pack_group_id`, que no cambia nunca) sigue intacto.
   *
   * Se quita PRIMERO: un regalo espejo puede comer del mismo inventario que otro producto en el
   * carrito, y con la linea vieja todavia presente el chequeo de sobreventa del agregado ve la
   * demanda de las dos. Si el agregado falla, se repone la linea vieja tal cual, talle incluido:
   * un cambio de talle nunca puede dejar al cliente con menos de lo que tenia.
   * @param {string} queueKey
   * @param {number} line
   * @param {string} newSize
   * @param {string | undefined} previousSize
   */
  async #applyPackSize(queueKey, line, newSize, previousSize) {
    const groupId = queueKey.startsWith('line:') ? undefined : queueKey;
    const row = this.#getRowByGroupId(groupId) ?? this.#getRowByLine(line);
    if (!row) return;

    /** @type {Record<string, {id: number, gifts: Array<{slot: number, id: number}>}> | null} */
    let sizeMap = null;
    try {
      sizeMap = JSON.parse(row.dataset.packSizeMap || 'null');
    } catch (error) {
      console.error('[cart-items] No se pudo leer el mapa de talles de la linea:', error);
      return;
    }

    const target = sizeMap?.[newSize];
    if (!target?.id) {
      // El talle no existe para este pack. No deberia poder elegirse —el marcado no emite esa
      // opcion—, asi que llegar aca significa un DOM viejo: se repinta desde el servidor en vez
      // de mandar un agregado que va a fallar.
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
      return;
    }

    const anchorKey = row.dataset.key;
    const currentVariantId = Number(row.dataset.variantId);
    const quantity = Number(row.dataset.rawQuantity) || 1;
    const baseHandle = row.dataset.packBaseHandle || undefined;
    const oldSize = previousSize || row.dataset.packSize || undefined;
    if (!anchorKey || !currentVariantId) return;

    const giftRows = this.#getGiftRows(row, groupId).filter((gift) => !gift.ownSize);
    const oldItems = [
      { id: currentVariantId, quantity, properties: buildPackAnchorProperties({ size: oldSize }, groupId) ?? {} },
      ...buildGiftItems(
        giftRows.map((gift) => ({ ...gift, size: oldSize })),
        { groupId, baseHandle }
      ),
    ];
    const newItems = [
      { id: target.id, quantity, properties: buildPackAnchorProperties({ size: newSize }, groupId) ?? {} },
      ...buildGiftItems(
        giftRows.map((gift) => ({
          ...gift,
          variantId: target.gifts?.find((candidate) => candidate.slot === gift.slot)?.id ?? gift.variantId,
          size: newSize,
        })),
        { groupId, baseHandle }
      ),
    ];

    this.#disableCartItems();
    let succeeded = false;
    try {
      const { remaining } = await this.#removeLines(
        [anchorKey, ...giftRows.map((gift) => gift.key)],
        { skipMorph: true }
      );
      if (remaining.length > 0) {
        this.#handleCartError(line, { errors: 'No se pudo cambiar el talle del pack' });
        return;
      }

      const parsed = await addPackItems(newItems, { sections: this.sectionId });
      if (parsed.status) {
        await addPackItems(oldItems).catch(
          /** @param {any} restoreError */ (restoreError) =>
            console.error('[cart-items] Restore after a failed size change also failed:', restoreError)
        );
        this.dispatchEvent(
          new CartErrorEvent({
            error: parsed.message || parsed.description || 'No se pudo cambiar el talle del pack',
            code: 'INVALID',
          })
        );
        return;
      }

      await this.#morphFromResponse(parsed);
      succeeded = true;
      if (previousSize) {
        // Nada mas que trazabilidad si algo sale mal a mitad de una cadena de cambios.
        console.debug(`[cart-items] Talle del pack ${groupId ?? line}: ${previousSize} a ${newSize}`);
      }
    } catch (error) {
      console.error(error);
      this.dispatchEvent(new CartErrorEvent({ error: this.#cartErrorMessage(error), code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#enableCartItems();
      if (!succeeded) await this.#recoverFromServer();
    }
  }

  /**
   * Misma cola que todo lo demas del pack. Un cambio de talle de regalo no cambia precios, pero
   * SI quita y agrega una linea, asi que tiene que serializarse contra el stepper y contra un
   * cambio de talle del ancla o los dos leerian un carrito a medio aplicar.
   * @param {string | undefined} groupId
   * @param {number} line
   * @param {string} newSize
   * @param {string | undefined} previousSize
   * @param {number} slot
   */
  #queueGiftSize(groupId, line, newSize, previousSize, slot) {
    const queueKey = groupId ?? `line:${line}`;
    const previousForPack = this.#packQueue.get(queueKey) ?? Promise.resolve();
    const next = Promise.allSettled([previousForPack, this.#packOpTail]).then(() =>
      this.#applyGiftSize(queueKey, line, newSize, previousSize, slot)
    );
    this.#packQueue.set(queueKey, next);
    this.#packOpTail = next;
    return next;
  }

  /**
   * Cambia el talle de UNA linea de regalo, sin tocar el pack que la sostiene: quitar esa linea y
   * agregarla en la variante del talle nuevo, con las mismas propiedades. El ancla no se toca:
   * su vinculo con el regalo es el `_pack_group_id`, que no cambia.
   * @param {string} queueKey
   * @param {number} line
   * @param {string} newSize
   * @param {string | undefined} previousSize
   * @param {number} slot
   */
  async #applyGiftSize(queueKey, line, newSize, previousSize, slot) {
    const groupId = queueKey.startsWith('line:') ? undefined : queueKey;
    const row = this.#getRowByLine(line);
    if (!row) return;

    /** @type {Record<string, {id: number}> | null} */
    let sizeMap = null;
    try {
      sizeMap = JSON.parse(row.dataset.packSizeMap || 'null');
    } catch (error) {
      console.error('[cart-items] No se pudo leer el mapa de talles del regalo:', error);
      return;
    }

    const target = sizeMap?.[newSize];
    if (!target?.id) {
      // El talle no existe para este regalo: el DOM esta viejo. Se repinta desde el servidor en
      // vez de mandar un agregado que va a fallar.
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
      return;
    }

    const key = row.dataset.key;
    const currentVariantId = Number(row.dataset.variantId);
    if (!key || !currentVariantId) return;
    const ownerGroup = row.dataset.packOwnerGroup || groupId;
    const baseHandle = row.dataset.packBaseHandle || undefined;
    const gift = {
      slot,
      quantity: Number(row.dataset.rawQuantity) || 1,
      isBundle: row.dataset.giftBundle === 'true',
      stockHandle: row.dataset.giftStockHandle || undefined,
    };
    const newItems = buildGiftItems([{ ...gift, variantId: target.id, size: newSize }], { groupId: ownerGroup, baseHandle });
    const oldItems = buildGiftItems([{ ...gift, variantId: currentVariantId, size: previousSize }], {
      groupId: ownerGroup,
      baseHandle,
    });

    this.#disableCartItems();
    let succeeded = false;
    try {
      const { remaining } = await this.#removeLines([key], { skipMorph: true });
      if (remaining.length > 0) {
        this.#handleCartError(line, { errors: 'No se pudo cambiar el talle del regalo' });
        return;
      }

      const parsed = await addPackItems(newItems, { sections: this.sectionId });
      if (parsed.status) {
        // La linea vieja ya no esta y la nueva no entro: se repone la vieja en su talle anterior,
        // o el cliente se queda sin el regalo.
        await addPackItems(oldItems).catch(
          /** @param {any} restoreError */ (restoreError) =>
            console.error('[cart-items] Restore after a failed gift size change also failed:', restoreError)
        );
        this.dispatchEvent(
          new CartErrorEvent({
            error: parsed.message || parsed.description || 'No se pudo cambiar el talle del regalo',
            code: 'INVALID',
          })
        );
        return;
      }

      await this.#morphFromResponse(parsed);
      succeeded = true;
      if (previousSize) {
        console.debug(`[cart-items] Talle del regalo (slot ${slot}): ${previousSize} a ${newSize}`);
      }
    } catch (error) {
      console.error(error);
      this.dispatchEvent(new CartErrorEvent({ error: this.#cartErrorMessage(error), code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#enableCartItems();
      if (!succeeded) await this.#recoverFromServer();
    }
  }

  /**
   * @param {string | undefined} groupId
   * @param {number} line
   * @param {PackTier[]} tiers
   * @param {number} newQuantity
   */
  #queuePackQuantity(groupId, line, tiers, newQuantity) {
    // Lines from before this pack got a `groupId` (already in a returning customer's cart
    // when this shipped) fall back to a line-keyed bucket — not as robust, but no worse than
    // before, and every NEW add tags `groupId` going forward.
    const queueKey = groupId ?? `line:${line}`;
    const previousForPack = this.#packQueue.get(queueKey) ?? Promise.resolve();
    // `allSettled` so a prior failure — of this pack OR any other — can't wedge later requests.
    const next = Promise.allSettled([previousForPack, this.#packOpTail]).then(() =>
      this.#applyPackQuantity(queueKey, line, tiers, newQuantity)
    );
    this.#packQueue.set(queueKey, next);
    this.#packOpTail = next;
    return next;
  }

  /**
   * Handles a quantity change on a line whose product has pack tiers. Always called through
   * {@link #queuePackQuantity} — never call this directly.
   *
   * La cantidad nueva del ancla y TODO ajuste de regalo que se pueda expresar como cantidad —un
   * slot que sobra, un duplicado, un regalo cuya cantidad cambia entre dos tiers— viajan en UNA
   * sola `/cart/update.js`, clave por clave de linea. Solo un slot que todavia no existe necesita
   * una segunda request (`/cart/add.js`). La clave del ancla no se mueve (la variante y las
   * propiedades no cambian), asi que no hay swap, ni identidad que regenerar, ni lectura previa
   * de `/cart.json` que hacer: lo que hay en el carrito lo dicen las filas del DOM, y lo que
   * quedo lo dice la respuesta.
   * @param {string} queueKey
   * @param {number} line
   * @param {PackTier[]} tiers
   * @param {number} newQuantity
   */
  async #applyPackQuantity(queueKey, line, tiers, newQuantity) {
    const groupId = queueKey.startsWith('line:') ? undefined : queueKey;
    const row = this.#getRowByGroupId(groupId) ?? this.#getRowByLine(line);
    const quantitySelector = row?.querySelector('cart-quantity-selector-component');
    // `row.dataset.rawQuantity` (see cart-item-row.liquid), NOT the quantity-selector's own
    // `data-cart-quantity` — that one comes from Horizon's `item_count_for_variant` filter,
    // which sums quantity across EVERY cart line sharing this variant, not just this row.
    // Silently wrong (and, confirmed live, enough to make a real increase get misread as a
    // decrease) whenever two separate packs of the same product sit in the cart.
    const previousQuantity = Number(row?.dataset.rawQuantity) || 0;
    const anchorKey = row?.dataset.key;

    const { tier, quantity } = resolveTierForQuantity(tiers, newQuantity);

    // The resolved state IS the current state — there is genuinely nothing to send. Reachable
    // when "−" is pushed below the smallest tier's own quantity. Without this the click still
    // costs a round trip that lands on the value the line already had.
    if (quantity === previousQuantity) {
      // `component-quantity-selector.js` writes the requested value optimistically before
      // dispatching and never rolls it back, so without this the input would keep showing a
      // number the cart doesn't actually have until the next unrelated repaint.
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) quantityInput.value = String(previousQuantity);

      // Take the "updating" treatment back off: `#onQuantityChange` shimmers the row's price
      // unconditionally and only a real server response clears it, and this branch
      // deliberately sends nothing. The row is already showing its true values.
      resetShimmer(this);
      delete this.dataset.optimisticPaint;
      return;
    }

    if (!row || !anchorKey) {
      // Refuse to guess. Without the row's stable key the only way to target the line would be
      // its POSITION, and a pack's gift renders adjacent to its anchor with `cart.items` order
      // not guaranteed — a stale position can land the anchor's new quantity on the GIFT line
      // (observed once: anchor 4 and "GRATIS x4" side by side). A repaint costs one retry.
      await this.#recoverFromServer();
      return;
    }

    this.#disableCartItems();
    let succeeded = false;

    try {
      const currentGifts = this.#getGiftRows(row, groupId);
      const wanted = this.#giftsForTier(tier, row);
      /** @type {Record<string, number>} */
      const updates = { [anchorKey]: quantity };
      /** @type {Array<PackTierGift & {size?: string}>} */
      const additions = [];
      const claimed = new Set();

      // Por SLOT, nunca por variante (ver `buildGiftItems`). Un slot que sigue siendo el mismo
      // producto se conserva —incluida la variante que el cliente eligio si el regalo tiene
      // talle propio— y solo se le ajusta la cantidad; otro producto en el mismo slot, o un
      // duplicado, se quita; un slot nuevo se agrega.
      for (const gift of wanted) {
        const sameSlot = currentGifts.filter((current) => current.slot === gift.slot);
        const keep = sameSlot.find((current) => current.productId === gift.productId);
        for (const current of sameSlot) {
          if (current !== keep) updates[current.key] = 0;
        }
        if (keep) {
          claimed.add(keep.key);
          if (keep.quantity !== gift.quantity) updates[keep.key] = gift.quantity;
        } else {
          additions.push(gift);
        }
      }
      for (const current of currentGifts) {
        if (!claimed.has(current.key) && updates[current.key] === undefined) updates[current.key] = 0;
      }

      const parsed = await this.#applyLineUpdates(updates, { skipMorph: additions.length > 0 });
      /** @type {any} */
      let snapshot = parsed;

      if (additions.length > 0) {
        const items = buildGiftItems(additions, { groupId, baseHandle: row.dataset.packBaseHandle || undefined });
        const added = await addPackItems(items, { sections: this.sectionId });
        if (added.status) {
          // The anchor's new quantity already landed; only the new gift didn't. Say so and paint
          // the real state (the pack without that gift) rather than the optimistic one.
          this.dispatchEvent(
            new CartErrorEvent({
              error: added.message || added.description || 'No se pudo agregar el regalo del pack',
              code: 'INVALID',
            })
          );
          await this.#recoverFromServer();
        } else {
          await this.#morphFromResponse(added);
        }
        // An add response carries only the added lines, so the update's snapshot is stale now.
        snapshot = null;
      }
      succeeded = true;

      // Self-repair from the response the operation itself got back — no extra read. A removal
      // here is rare (a duplicate or a stranded dependent left behind by an earlier failure).
      if (snapshot?.items && (await this.#reconcilePackDependents(groupId, snapshot)).removed > 0) {
        await this.#recoverFromServer();
      }
    } catch (error) {
      console.error(error);
      try {
        this.#handleCartError(line, { errors: this.#cartErrorMessage(error) });
      } catch {
        this.dispatchEvent(new CartErrorEvent({ error: this.#cartErrorMessage(error), code: 'SERVICE_UNAVAILABLE' }));
      }
    } finally {
      if (!succeeded) await this.#recoverFromServer();
      this.#enableCartItems();
    }
  }

  /**
   * Los regalos que le corresponden a una linea parada en `tier`, con el talle del pack para
   * los que lo espejan. Un regalo de talle propio recien agregado desde aca sale en el talle que
   * eligio Liquid (`size` en `data-pack-tiers`: el primero con stock para su cantidad), y ese
   * talle viaja como `_pack_size` para que sus unidades se descuenten del stock; la fila que
   * llega trae su selector para cambiarlo.
   * @param {PackTier} tier
   * @param {HTMLTableRowElement} row
   * @returns {Array<PackTierGift & {size?: string}>}
   */
  #giftsForTier(tier, row) {
    const size = row.dataset.packSize || undefined;
    return (tier.gifts ?? []).map((gift, index) => ({
      slot: Number(gift.slot ?? index),
      variantId: Number(gift.variantId),
      productId: Number(gift.productId) || 0,
      quantity: Math.max(1, Number(gift.quantity) || 1),
      isBundle: Boolean(gift.isBundle),
      stockHandle: gift.stockHandle || undefined,
      size: gift.size || size,
    }));
  }

  /**
   * Un `/cart/update.js` con varias lineas a la vez, clave por clave: la cantidad nueva del ancla
   * y los ajustes de sus regalos viajan juntos, y Shopify devuelve el carrito ENTERO mas la
   * seccion re-renderizada, asi que la respuesta es a la vez la verificacion y el repintado.
   *
   * Emite el `CartLinesUpdateEvent` con la seccion adentro, para que el otro
   * `cart-items-component` (cajon y pagina `/cart` a la vez) morfee sin pedir nada.
   * @param {Record<string, number>} updates - clave de linea → cantidad (0 = quitar)
   * @param {object} [options]
   * @param {boolean} [options.skipMorph] - el caller repinta el mismo con una respuesta posterior
   * @returns {Promise<any>} la respuesta parseada (el carrito, con `sections`)
   */
  async #applyLineUpdates(updates, { skipMorph = false } = {}) {
    const marker = cartPerformance.createStartingMarker('change:user-action');
    const body = JSON.stringify({
      updates,
      sections: Array.from(this.#sectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    // El validador de eventos estándar de Shopify rechaza una línea con cantidad 0 dentro de un
    // `update` ("Too small: expected number to be >0"), y un evento rechazado no llega a nadie:
    // el contador del header se quedaría viejo. Las bajas viajan igual en la request; el evento
    // sólo anuncia las líneas que quedan, y es un `remove` cuando no queda ninguna.
    const announced = Object.entries(updates)
      .filter(([, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({ id, quantity }));
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: announced.length > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: announced,
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const parsed = await response.json();
      resetShimmer(this);

      if (parsed.status || parsed.errors) {
        throw new Error(parsed.errors || parsed.description || parsed.message || 'No se pudo actualizar el carrito');
      }

      this.#updateQuantitySelectors(parsed);

      // See the matching try/catch in `#removeLines` — Shopify's own `createCartFromAjaxResponse`
      // has thrown for this store under some response shapes, and letting that escape would skip
      // the morph below, leaving the DOM stale even though the update already succeeded.
      try {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed),
          detail: {
            sections: parsed.sections,
            items: parsed.items,
            // El carrito ENTERO tal como lo devolvió `/cart/update.js` (misma forma que
            // `/cart.js`): los que escuchan (contador del header, barra de envío gratis, ficha)
            // lo leen de acá en vez de volver a pedir `/cart.js` después de cada cambio.
            cartData: parsed,
            itemCount: this.#itemCountFromSections(parsed.sections),
            source: 'cart-items-component',
            didError: false,
          },
        });
      } catch (resolveError) {
        console.error('CartLinesUpdateEvent.createCartFromAjaxResponse failed:', resolveError);
        deferredUpdatePromise.reject(resolveError);
      }

      if (!skipMorph && parsed.sections?.[this.sectionId]) {
        // Awaited — a queued pack operation chained after this one must never read the DOM
        // before this morph has actually landed (stale `data-key`/`data-raw-quantity`).
        await morphSection(this.sectionId, parsed.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        this.#updateCartQuantitySelectorButtonStates();
      }

      return parsed;
    } catch (error) {
      deferredUpdatePromise.reject(error);
      throw error;
    } finally {
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * Repinta con la seccion que trajo una respuesta de `/cart/add.js` y avisa del cambio.
   *
   * Un agregado no emite evento propio (a diferencia de `updateQuantity`/`#removeLines`): sin
   * esto el contador del header y los recomendados se quedan viejos hasta la proxima accion del
   * carrito. El `add` no devuelve el carrito entero y el validador de eventos de Shopify exige
   * un carrito completo y al menos una linea, asi que se lee `/cart.json` UNA vez — DESPUES del
   * morph, fuera del camino critico del repintado, y solo en las operaciones que agregan (un
   * regalo que aparece, un cambio de talle).
   * Siempre como `update` con claves de línea: para un `add` el validador exige `merchandiseId`
   * como GID de variante, y lo que cambió acá son líneas del carrito.
   * @param {any} parsed
   */
  async #morphFromResponse(parsed) {
    const sectionHtml = parsed?.sections?.[this.sectionId];
    if (sectionHtml) {
      await morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      await this.#recoverFromServer();
    }

    const added = /** @type {any[]} */ (parsed?.items ?? []);
    if (added.length === 0) return;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: added.map((item) => ({ id: String(item.key ?? item.variant_id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      const cart = await this.fetchCartData();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: parsed?.sections,
          items: cart.items,
          cartData: cart,
          itemCount: this.#itemCountFromSections(parsed?.sections),
          source: 'cart-items-component',
          didError: false,
        },
      });
    } catch (error) {
      // Los que escuchan (contador, recomendados) se quedan sin esta actualizacion y se corrigen
      // en la proxima accion del carrito; el repintado de arriba ya esta hecho.
      console.error('[cart-items] Cart read after add failed:', error);
      deferred.reject(error);
    }
  }

  /**
   * Vuelve a pedirle la seccion al servidor y la pinta. Es el camino de recuperacion de toda
   * operacion que no termino bien: pone el estado REAL en pantalla en vez de dejar el pintado
   * optimista de un cambio que el carrito nunca acepto —que se lee como el carrito trabado
   * cargando un numero que nunca llega.
   */
  async #recoverFromServer() {
    await sectionRenderer
      .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
      .catch(/** @param {any} error */ (error) => console.error('[cart-items] Recovery re-render failed:', error));
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Los ids de seccion de TODOS los `cart-items-component` de la pagina (cajon y `/cart` a la
   * vez), para pedirlos en una misma request y que el otro morfee sin pedir nada.
   * @returns {Set<string>}
   */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });
    return sectionsToUpdate;
  }

  /**
   * El conteo del carrito leido del markup que la propia respuesta ya trae, sin otra request.
   * @param {Record<string, string> | undefined} sections
   * @returns {number}
   */
  #itemCountFromSections(sections) {
    const html = sections?.[this.sectionId];
    if (!html) return 0;
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const raw = parsedHtml.querySelector('[ref="cartItemCount"]')?.textContent;
    return raw ? parseInt(raw, 10) || 0 : 0;
  }

  /**
   * Whether a cart line is a gift of the given pack.
   *
   * Two links are accepted, and either one is enough: `_pack_group_id` (the pack's stable
   * identity) and `_pack_parent_id` (the anchor's `_pack_id`, which carries the same value).
   * @param {any} item
   * @param {string | undefined} packId
   * @param {string | undefined} groupId
   */
  #belongsToPack(item, packId, groupId) {
    const properties = item?.properties;
    if (!properties?._pack_kind) return false;
    if (groupId && properties._pack_group_id === groupId) return true;
    return Boolean(packId) && properties._pack_parent_id === packId;
  }

  /**
   * Brings a pack's dependent lines back in line with reality after an operation, and clears
   * dependents whose pack is gone entirely.
   *
   * This is the part that makes the cart self-healing rather than merely careful. Every other
   * guard in this file tries to stop a specific race from breaking the anchor↔dependent link;
   * this one assumes a break may have happened anyway — from a dropped request, or a concurrent
   * write by a third-party app (confirmed to happen on this store, see `#removeLines`) — and
   * repairs it. It is idempotent, so it converges on the correct state no matter which step
   * lost which race. Solo QUITA, nunca agrega: un regalo que falta lo agrega unicamente una
   * accion del cliente, porque "agregarlo porque no lo encontre" fue la causa raiz de los regalos
   * multiplicandose sin limite.
   *
   * Two repairs, deliberately scoped differently by risk:
   * - **Duplicate gifts** (same pack, same slot, more than one line): all but one removed.
   *   Always safe — a gift line is $0 by definition, validated at render time.
   * - **Stranded dependents** (no anchor in the cart matches by group OR by parent id):
   *   removed. This is the same cascade `onLineItemRemove` already performs when an anchor is
   *   deleted normally; it just also catches the ones a failed operation left behind.
   *
   * @param {string} [groupId] - the pack just operated on, if known; other packs are still
   *   checked for stranded lines, since a failure on one can strand another's dependents.
   * @param {any} [cartSnapshot] - the cart as a response just returned it. When given, no read
   *   is made: the operation's own response is already the truth.
   * @returns {Promise<{removed: number, cart: any}>}
   */
  async #reconcilePackDependents(groupId, cartSnapshot) {
    /** @type {any} */
    let cart = null;
    try {
      cart = cartSnapshot?.items ? cartSnapshot : await this.fetchCartData();
      const anchors = cart.items.filter(/** @param {any} i */ (i) => i.properties?._pack_id);
      const anchorPackIds = new Set(anchors.map(/** @param {any} a */ (a) => a.properties._pack_id));
      const anchorGroupIds = new Set(
        anchors.map(/** @param {any} a */ (a) => a.properties._pack_group_id).filter(Boolean)
      );

      /** @type {string[]} */
      const toRemove = [];
      /** @type {Set<string>} */
      const keptGifts = new Set();

      for (const item of cart.items) {
        const kind = item.properties?._pack_kind;
        if (!kind) continue;

        const itemGroup = item.properties?._pack_group_id;
        const itemParent = item.properties?._pack_parent_id;
        const stranded = !(itemGroup && anchorGroupIds.has(itemGroup)) && !(itemParent && anchorPackIds.has(itemParent));

        if (stranded) {
          toRemove.push(item.key);
          continue;
        }

        if (kind !== 'gift') continue;
        // One gift line per (pack, gift SLOT). Without a group id there is nothing stable to
        // group by, so such a line is left alone rather than guessed at.
        const owner = itemGroup && anchorGroupIds.has(itemGroup) ? itemGroup : null;
        if (!owner) continue;
        // The gift's slot in its tier's gift list — NOT its variant id: a size change moves the
        // variant, and two lines of the SAME gift in two sizes would otherwise both be kept.
        // Falls back to the variant id for lines added before `_pack_gift_slot` existed.
        const slot = item.properties?._pack_gift_slot ?? `v${item.variant_id}`;
        const key = `${owner}::${slot}`;
        if (keptGifts.has(key)) toRemove.push(item.key);
        else keptGifts.add(key);
      }

      if (toRemove.length === 0) return { removed: 0, cart };
      console.warn(
        `[cart-items] Reconciled pack dependents${groupId ? ` (group ${groupId})` : ''}: removing ${toRemove.length} duplicate/stranded line(s).`
      );
      await this.#removeLines(toRemove, { skipMorph: true });
      return { removed: toRemove.length, cart: null };
    } catch (error) {
      // Never let self-repair break the operation it was meant to protect — the customer's
      // actual change has already landed by the time this runs.
      console.error('[cart-items] Pack dependent reconciliation failed:', error);
      return { removed: 0, cart };
    }
  }

  /**
   * Removes multiple cart lines by their stable keys in a single request — Shopify's
   * `/cart/update.js` accepts a key-keyed `updates` map, so several lines can be cleared
   * together instead of one `/cart/change.js` round trip each.
   *
   * Pass `skipMorph` when the caller is going to morph to the final state itself moments later:
   * the intermediate repaint is wasted work and the customer sees a flash of "the gift is gone
   * but the new line isn't here yet" in between.
   *
   * A 200 response here does NOT reliably mean the removal actually happened — confirmed live:
   * with another app also issuing its own concurrent `/cart/update.js` calls on this cart (a
   * discount app setting an unrelated attribute), a cleanup removal occasionally came back 200
   * with the "removed" keys still present in `parsed.items`, no error anywhere. So this
   * re-checks the response for each targeted key and retries the ones still present.
   * @param {string[]} keys
   * @param {object} [options]
   * @param {boolean} [options.skipMorph] - caller morphs to the final state itself
   * @returns {Promise<{remaining: string[], parsed: any}>} the keys that could NOT be removed,
   *   plus the last response (the whole cart, with `sections`) — the caller can reconcile from
   *   it without another read.
   */
  async #removeLines(keys, { skipMorph = false } = {}) {
    if (keys.length === 0) return { remaining: [], parsed: null };

    const sectionsToUpdate = this.#sectionsToUpdate();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: keys.map((key) => ({ id: key, quantity: 0 })),
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      let remaining = keys;
      /** @type {any} */
      let parsed;
      for (let attempt = 1; attempt <= 3; attempt++) {
        const updates = Object.fromEntries(remaining.map((key) => [key, 0]));
        const body = JSON.stringify({
          updates,
          // Only the final repaint needs markup; a removal the caller is about to overwrite with
          // its own response would make the server render the whole section for nothing.
          ...(skipMorph
            ? {}
            : { sections: Array.from(sectionsToUpdate).join(','), sections_url: window.location.pathname }),
        });
        const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
        parsed = await response.json();

        remaining = remaining.filter(
          (key) => parsed.items?.some(/** @param {any} item */ (item) => item.key === key)
        );
        if (remaining.length === 0) break;
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
      }

      // Shopify's own `createCartFromAjaxResponse` (from the platform's standard-events
      // bundle, not our code) has thrown here for this store under some response shapes
      // ("Currency code is required with currency style") — confirmed live, unrelated to
      // whether the removal itself succeeded. Isolated so that bug can never skip the morph
      // below, which is what actually keeps the visible cart in sync with the real server
      // state.
      try {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed),
          detail: {
            sections: parsed.sections,
            items: parsed.items,
            cartData: parsed,
            itemCount: this.#itemCountFromSections(parsed.sections),
            source: 'cart-items-component',
            didError: remaining.length > 0,
          },
        });
      } catch (resolveError) {
        console.error('CartLinesUpdateEvent.createCartFromAjaxResponse failed:', resolveError);
        deferredUpdatePromise.reject(resolveError);
      }

      if (remaining.length > 0) {
        console.error('Failed to remove cart lines after retries:', remaining);
        this.dispatchEvent(
          new CartErrorEvent({ error: 'No se pudo actualizar el carrito, actualizá la página', code: 'INVALID' })
        );
      }

      if (!skipMorph && parsed.sections?.[this.sectionId]) {
        // Awaited — the pack-line queue relies on THIS function's promise only resolving once
        // the repaint is genuinely done, so a chained next-in-queue operation never reads the
        // DOM mid-morph (stale `data-key`, confirmed by direct testing).
        await morphSection(this.sectionId, parsed.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
      }
      if (!skipMorph) this.#updateCartQuantitySelectorButtonStates();

      // Reported rather than thrown: exhausting the retries is NOT an exception here (the
      // request itself succeeded — Shopify returned 200 and simply didn't apply the removal, a
      // confirmed real behaviour on this store). Callers that would be left in a broken
      // half-state by it check `remaining` and repair.
      return { remaining, parsed };
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);
      throw error;
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getRowByLine(line);
    const packId = cartItemRowToRemove?.dataset.packId;
    const groupId = cartItemRowToRemove?.dataset.packGroupId;
    // Resolved NOW, synchronously, while the DOM still matches what the customer actually
    // clicked. `line` is a render-time position (`cart-item-row.liquid`'s `data-line`) and the
    // request below no longer runs immediately, so by then the position can point elsewhere.
    const key = cartItemRowToRemove?.dataset.key;

    // A pack's gifts, resolved from the DOM right here rather than from a `/cart.js`
    // read inside the request chain. That read was what forced removal to be three sequential
    // round trips (delete anchor → read cart → delete dependents); measured on a throttled
    // mobile profile, the gift stayed in the cart 763 ms after the anchor was already gone —
    // enough time to press Checkout and reach payment still holding a free gift.
    const dependentRows = this.#getDependentRows(cartItemRowToRemove, packId, groupId);
    const dependentKeys = dependentRows.map((row) => row.dataset.key).filter(Boolean);

    // Serialized on the same global tail as every pack operation, and the cart stays locked
    // until the dependent cascade has ALSO finished. Run outside the queue, a removal could
    // interleave with an in-flight size change (which deletes lines before re-adding them), and
    // a second trash click landing in that gap targeted a position about to be invalidated.
    const next = Promise.allSettled([this.#packOpTail]).then(async () => {
      this.#disableCartItems();
      try {
        /** @type {any} */
        let snapshot = null;
        if (key && dependentKeys.length > 0) {
          // One `/cart/update.js` with a keyed `updates` map clears the anchor and every
          // dependent together, so there is no intermediate cart state in which the gift
          // exists without the pack that justifies it.
          ({ parsed: snapshot } = await this.#removeLines([key, ...dependentKeys]));
        } else {
          // A row that carries its group id is authoritative: no dependent rows means no
          // dependents, and the reconciliation below still catches anything stranded.
          snapshot = (await this.updateQuantity({ line, ...(key ? { key } : {}), quantity: 0, action: 'clear' })) ?? null;
        }

        // Catches anything the cascade above could not resolve — e.g. a dependent whose
        // parent link was already broken before this removal, which would otherwise stay in
        // the cart forever as a free item with nothing to attach to. Reads from the response
        // the removal itself returned, so it costs no request unless it finds something.
        if ((await this.#reconcilePackDependents(groupId, snapshot)).removed > 0) {
          await this.#recoverFromServer();
        }
      } catch (error) {
        // Las filas ya se animaron fuera de la pantalla antes de la request (mas abajo): si la
        // baja no llego a aplicarse, el cliente veria un carrito vacio que el servidor todavia
        // tiene lleno. Se vuelve a pintar el estado real.
        console.error('[cart-items] Line removal failed:', error);
        await this.#recoverFromServer();
      } finally {
        this.#enableCartItems();
      }
    });
    this.#packOpTail = next;

    if (!cartItemRowToRemove) return;

    // The gift rows animate out in the SAME frame as the anchor. Previously this list only
    // matched `data-parent-key` — Shopify's own bundle parent relationship, which a pack's
    // gift never has — so the anchor vanished instantly while its gift sat visible for over a
    // second, which is what made the cart look like it still owed the customer something.
    const rowsToRemove = [cartItemRowToRemove, ...dependentRows];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - Target by stable cart line key instead of position, when
   *   known — safer whenever another mutation may have shifted `cart.items` ordering since
   *   `line` was captured, since `/cart/change.js`'s `id` accepts a line key.
   * @param {Record<string, string>} [config.properties] - When present, replaces the line's
   *   ENTIRE properties object (Shopify's `/cart/change.js` doesn't merge).
   * @returns {Promise<any>} the parsed response (the whole cart, with `sections`), or
   *   `undefined` when the request failed
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, key, properties } = config;
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      ...(key ? { id: key } : { line: line }),
      quantity: quantity,
      ...(properties ? { properties } : {}),
      sections: Array.from(this.#sectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.#getRowByLine(line)?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return undefined;
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // See the matching try/catch in `#removeLines` — Shopify's own
        // `createCartFromAjaxResponse` has thrown for this store under some response shapes,
        // and letting that escape here would skip the morph below via the outer catch,
        // leaving the DOM stale even though the update itself already succeeded.
        try {
          deferredUpdatePromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
            detail: {
              sections: parsedResponseText.sections,
              items: parsedResponseText.items,
              cartData: parsedResponseText,
              itemCount: this.#itemCountFromSections(parsedResponseText.sections),
              source: 'cart-items-component',
              didError: false,
            },
          });
        } catch (resolveError) {
          console.error('CartLinesUpdateEvent.createCartFromAjaxResponse failed:', resolveError);
          deferredUpdatePromise.reject(resolveError);
        }

        // Awaited — a queued pack-line operation chained after this one (see
        // `#queuePackQuantity`) must never read the DOM before this morph has actually landed
        // (stale `data-variant-id`/`data-key`, confirmed by direct testing).
        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: this.#cartErrorMessage(error),
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        return undefined;
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * User-facing message for a failed cart request. A timed-out request (see
   * `CART_FETCH_TIMEOUT_MS`) is the one failure the shopper can actually act on, so it gets
   * its own wording rather than surfacing the raw `TimeoutError` text.
   * @param {any} error
   * @returns {string}
   */
  #cartErrorMessage(error) {
    if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
      return 'La conexión tardó demasiado. Revisá tu internet y volvé a intentar.';
    }
    return error?.message || 'No se pudo actualizar el carrito';
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Pack gift rows have no quantity-selector at all (read-only, see
    // cart-products.liquid) — nothing to revert there, so this is intentionally optional.
    const quantitySelector = this.#getRowByLine(line)?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** Reference count for {@link #disableCartItems}/{@link #enableCartItems} — a multi-step
   * sequence calls `updateQuantity()` internally, and that method unconditionally re-enables on
   * its own completion; without counting, the cart would briefly become interactive again
   * between the sequence's own steps, letting a second operation start before the first one's
   * cleanup finished. */
  #disableDepth = 0;

  /** `performance.now()` at the 0→1 transition of {@link #disableDepth}, for the stuck-lock
   * watchdog below. `null` whenever the cart is idle. */
  #disableStartedAt = null;

  /** Upper bound on how long the cart may legitimately stay locked. Every request in a pack
   * sequence is capped at `CART_FETCH_TIMEOUT_MS`, and a sequence issues at most a handful,
   * so anything past this means a `finally` never ran and the lock leaked. */
  static #MAX_DISABLE_MS = 20000;

  /**
   * Drops any lock this component is still holding, without waiting for the operation that took
   * it to settle. Public because the only caller is the module-level `pageshow` handler at the
   * bottom of this file — see the comment there for why a restored page can arrive holding a
   * lock that nothing will ever release.
   *
   * Resets `#disableDepth` too, and that is the part that cannot be done from outside the class.
   * bfcache preserves the JS heap, so this instance comes back with its private fields intact:
   * clearing only the attributes and the class would leave the counter reading 1, and then the
   * NEXT operation would take it to 2 while its own `#enableCartItems` only brings it back to 1.
   * It would never reach 0 again, so the cart would stay visibly locked from then on — a worse
   * bug than the one being fixed, and one that would only show up on the second interaction.
   */
  releaseBusyState() {
    this.#disableDepth = 0;
    this.#disableStartedAt = null;
    this.classList.remove('cart-items-disabled');
    delete this.dataset.cartBusy;
    this.removeAttribute('aria-busy');
    delete this.dataset.optimisticPaint;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    // Watchdog: if the previous lock never released, the cart is dead (no stepper responds,
    // and for a pack line its `#packQueue` entry is wedged too) with no way back short of a
    // reload. Every await in the disable window is now timeout-capped, so reaching this is a
    // bug — recover rather than stay bricked, and say so loudly.
    if (
      this.#disableDepth > 0 &&
      this.#disableStartedAt !== null &&
      performance.now() - this.#disableStartedAt > CartItemsComponent.#MAX_DISABLE_MS
    ) {
      console.error(
        `[cart-items] Cart lock held for over ${CartItemsComponent.#MAX_DISABLE_MS}ms — forcing release. A cart operation never settled.`
      );
      this.#disableDepth = 0;
      this.classList.remove('cart-items-disabled');
      delete this.dataset.cartBusy;
      this.removeAttribute('aria-busy');
    }

    if (this.#disableDepth === 0) this.#disableStartedAt = performance.now();
    this.#disableDepth++;
    this.classList.add('cart-items-disabled');
    // A single durable attribute rather than setting `disabled` on each button. The old
    // per-button approach didn't survive: the morph copies attributes from the server's HTML,
    // which has no `disabled`, so `copyAttributes` stripped the lock on the first repaint
    // INSIDE the operation — leaving only `pointer-events: none` for the rest of it. This
    // attribute is in morph.js's `PRESERVED_ATTRIBUTES`, so it holds for the whole operation,
    // and it drives the visible "updating" treatment (see cart-products.liquid).
    this.dataset.cartBusy = '';
    this.setAttribute('aria-busy', 'true');
    this.#syncGlobalBusyState();
  }

  /**
   * Mirrors "a cart operation is in flight" onto the document root.
   *
   * `cart-items-disabled`'s `pointer-events: none` only covers this element's own subtree.
   * In the drawer that happens to include the checkout button (`cart-summary` is rendered
   * inside the component), but on the `/cart` page `_cart-products` and `_cart-summary` are
   * SIBLING blocks — so there the button stayed fully clickable for the whole operation, which
   * is exactly the window that let a shopper reach checkout mid-removal.
   *
   * Derived from the live DOM rather than a counter: two components (drawer and page) can be
   * busy at once, and whichever finished first would otherwise clear the flag while the other
   * was still working.
   */
  #syncGlobalBusyState() {
    const anyBusy = document.querySelector('cart-items-component[data-cart-busy]') !== null;
    document.documentElement.toggleAttribute('data-cart-busy', anyBusy);

    // Dead-man's switch. The flag disables the checkout button, so a lock that never released
    // would leave a shopper unable to pay at all until they reloaded — strictly worse than
    // before this flag existed. `#disableCartItems`'s own watchdog can't save them: it only
    // runs when a NEW operation starts, and a frozen cart is `pointer-events: none`, so no new
    // operation can ever start. Bounded here instead, on a timer that owes nothing to user input.
    clearTimeout(globalBusyReleaseTimer);
    if (anyBusy) {
      globalBusyReleaseTimer = setTimeout(() => {
        if (!document.documentElement.hasAttribute('data-cart-busy')) return;
        console.error(
          `[cart-items] Global cart-busy flag held for over ${CartItemsComponent.#MAX_DISABLE_MS}ms — releasing it so checkout stays reachable.`
        );
        document.documentElement.removeAttribute('data-cart-busy');
      }, CartItemsComponent.#MAX_DISABLE_MS);
    }
  }

  /**
   * Enables the cart items once every nested disable call has been matched by an enable call.
   */
  #enableCartItems() {
    this.#disableDepth = Math.max(0, this.#disableDepth - 1);
    if (this.#disableDepth === 0) {
      this.#disableStartedAt = null;
      this.classList.remove('cart-items-disabled');
      delete this.dataset.cartBusy;
      this.removeAttribute('aria-busy');
      this.#syncGlobalBusyState();
      delete this.dataset.optimisticPaint;
      // Let each selector recompute its own min/max-based disabled state (via
      // `updateButtonStates()`) instead of blanket re-enabling — a button legitimately
      // disabled for other reasons (e.g. server-disabled gift rows) must stay that way.
      this.#updateCartQuantitySelectorButtonStates();
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    // Delegado a `fetchCartJson` (utilities): mismo `no-store`, mismo timeout, y UNA sola lectura
    // en vuelo compartida con el contador del header, la barra de envío gratis y la ficha. Antes
    // este método tenía su propia caché de vuelo sobre `/cart.json` y aquéllos otra sobre
    // `/cart.js` —el mismo JSON por dos rutas—, así que cada operación del carrito pagaba dos
    // lecturas idénticas en paralelo (medido en vivo: `/cart.json` + `/cart.js`, 0,3-0,7 s cada
    // una en 4G, después de cada agregado y de cada cambio de talle).
    return fetchCartJson();
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/**
 * Clears a busy state that outlived the page it belonged to.
 *
 * REPORTED: tapping "Transferencia — 10% OFF", reaching the checkout and pressing Back left
 * that button greyed out and dead. Not the app's doing — it's ours. `#syncGlobalBusyState`
 * mirrors "a cart operation is in flight" onto `<html>` as `data-cart-busy`, and
 * `snippets/cart-summary.liquid` paints anything under that flag at `opacity: 0.6` with
 * `pointer-events: none` — the Scala button included, by name, because its script inserts it as
 * a SIBLING of `.cart__ctas` rather than inside.
 *
 * The app navigates to the checkout the instant it's clicked, so the page leaves with an
 * operation still open and the flag still set. bfcache stores the DOM verbatim, `<html>` and its
 * attributes included, so Back restores a page that believes the cart is still busy — and
 * nothing can ever clear it: `#enableCartItems` only runs when the promise that set the flag
 * settles, and that promise died with the previous page. The watchdog in `#syncGlobalBusyState`
 * can't save it either: it's a `setTimeout`, which bfcache freezes along with everything else.
 *
 * Runs on every `pageshow`, not just `event.persisted` ones. Horizon uses cross-document view
 * transitions, so `pageshow` fires on ordinary navigations too (see `assets/scroll-container.js`),
 * and a page that has just been painted cannot have a cart operation in flight — there has been
 * no time for one to start. Clearing unconditionally is therefore always correct, and it also
 * covers restores that never went through bfcache at all.
 */
window.addEventListener('pageshow', () => {
  document.documentElement.removeAttribute('data-cart-busy');

  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof CartItemsComponent) component.releaseBusyState();
  }
});

/** The cart's own checkout submit button. `name="checkout"` appears exactly once in the theme
 * (`snippets/cart-summary.liquid`), so this cannot collide with anything else. */
const CHECKOUT_TRIGGER_SELECTOR = ':is(button,input)[name="checkout"]';

/** Installed once per page, not per component — the drawer and the `/cart` page each mount
 * their own `cart-items-component`, and two guards would each cancel and replay the click. */
let checkoutGuardInstalled = false;

/**
 * Last line of defence before checkout: let any pack operation still in flight land, then let
 * the click through. Costs nothing on an ordinary cart: with no pack dependents rendered there
 * is nothing to wait for, and the click is never intercepted at all; with an idle cart the wait
 * resolves immediately.
 *
 * NOT a security boundary, and shouldn't be mistaken for one — this runs in the shopper's own
 * browser. It closes the accidental path (clicking Checkout mid-operation), not a determined
 * one. Real enforcement would need a server-side Shopify Function.
 */
function installCheckoutGuard() {
  if (checkoutGuardInstalled) return;
  checkoutGuardInstalled = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest(CHECKOUT_TRIGGER_SELECTOR);
      if (!(trigger instanceof HTMLElement)) return;
      // The replayed click after verification — let it run its normal course.
      if (trigger.dataset.packVerified === 'true') return;
      // No pack gift anywhere on the page: nothing this guard could wait for.
      if (!document.querySelector('.cart-items__nested-line')) return;

      const component = document.querySelector('cart-items-component');
      if (!(component instanceof CartItemsComponent)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      // Bounded as a whole: a slow or wedged operation must never strand a shopper on a dead
      // checkout button. Whatever happens, the click is replayed.
      Promise.race([component.verifyPackIntegrity(), new Promise((resolve) => setTimeout(resolve, 4000))])
        .catch(/** @param {any} error */ (error) => console.error('[cart-items] Checkout verification failed:', error))
        .finally(() => {
          // Re-query: a re-render may have replaced `trigger`.
          const fresh = document.querySelector(CHECKOUT_TRIGGER_SELECTOR);
          const button = fresh instanceof HTMLElement ? fresh : trigger;
          button.dataset.packVerified = 'true';
          button.click();
        });
    },
    true
  );
}

installCheckoutGuard();
